// src/providers/i18nProvider.ts
import polyglotI18nProvider from 'ra-i18n-polyglot';
import { withSmartCount } from './smartCount';

// Import modular translations (already include React Admin messages)
import es from '@/i18n/es';
import en from '@/i18n/en';

// Supported locales type
export type SupportedLocale = 'es' | 'en';

// Combined messages
const messages = {
  en,
  es,
};

// Available locales configuration
export const availableLocales = [
  { locale: 'es', name: 'Español' },
  { locale: 'en', name: 'English' },
];

// Default locale
const defaultLocale: SupportedLocale = 'es';

/**
 * Get saved locale from localStorage
 */
export const getSavedLocale = (): SupportedLocale => {
  const saved = localStorage.getItem('locale');
  if (saved && (saved === 'es' || saved === 'en')) {
    return saved as SupportedLocale;
  }
  
  // Detect browser language
  const browserLocale = navigator.language.split('-')[0];
  if (browserLocale === 'es' || browserLocale === 'en') {
    return browserLocale as SupportedLocale;
  }
  
  return defaultLocale;
};

/**
 * Save locale to localStorage
 */
export const saveLocale = (locale: SupportedLocale): void => {
  localStorage.setItem('locale', locale);
};

/**
 * Create i18n provider
 */
const baseI18nProvider = polyglotI18nProvider(
  locale => {
    // Log in development
    if (import.meta.env.DEV) {
      console.log('Loading locale:', locale);
    }
    
    // Save selected locale if it changed
    if (locale !== localStorage.getItem('locale')) {
      saveLocale(locale as SupportedLocale);
    }
    
    // Return messages for the locale or default
    return (messages as any)[locale] || messages[defaultLocale];
  },
  getSavedLocale(),
  availableLocales
);

const i18nProvider = {
  ...baseI18nProvider,
  translate: (key: string, options?: any) => baseI18nProvider.translate(key, withSmartCount(options)),
};

/**
 * Get a specific translation
 */
export const getTranslation = (key: string, locale?: SupportedLocale): string => {
  const currentLocale = locale || getSavedLocale();
  const messageObj = messages[currentLocale];
  
  // Navigate through the message object using dot notation
  const keys = key.split('.');
  let result: any = messageObj;
  
  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      // If not found, try with default locale
      if (currentLocale !== defaultLocale) {
        return getTranslation(key, defaultLocale);
      }
      return key; // Return key if translation not found
    }
  }
  
  return typeof result === 'string' ? result : key;
};

/**
 * Format a translation with parameters
 */
export const formatTranslation = (
  key: string,
  params: Record<string, any>,
  locale?: SupportedLocale
): string => {
  let translation = getTranslation(key, locale);
  
  // Replace parameters in %{param} format
  Object.entries(params).forEach(([param, value]) => {
    translation = translation.replace(`%{${param}}`, String(value));
  });
  
  return translation;
};

/**
 * Get translations for a specific resource
 */
export const getResourceTranslations = (
  resource: string,
  locale?: SupportedLocale
): Record<string, any> => {
  const currentLocale = locale || getSavedLocale();
  const messageObj = messages[currentLocale];
  
  if (messageObj.resources && messageObj.resources[resource]) {
    return messageObj.resources[resource];
  }
  
  // If not found, try with default locale
  if (currentLocale !== defaultLocale) {
    return getResourceTranslations(resource, defaultLocale);
  }
  
  return {};
};

/**
 * Custom hook to change locale
 */
export const useChangeLocale = () => {
  const changeLocale = (locale: SupportedLocale) => {
    saveLocale(locale);
    // Reload page to apply changes
    window.location.reload();
  };
  
  return changeLocale;
};

export default i18nProvider;